import React, {useLayoutEffect, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {createPortal} from 'react-dom';
import DateTimePicker from "./DateTimePicker";
import {dateToString} from "../../utils/DateUtils";

// Drop-in replacement for DateTimePicker's own click-to-open calendar. DateTimePicker's calendar
// uses `position: absolute`, which gets clipped if an ancestor has `overflow: hidden`/`auto`
// (e.g. a card's collapse-transition wrapper). This component keeps a normal trigger field in
// place, but renders the calendar through a React portal into document.body with
// `position: fixed`, centered under the trigger, so it always floats above everything.
//
// Optionally takes `keywordOptions` - when given, a scrollable "Keywords" column is shown next to
// the calendar in the same floating panel, height-matched to it. In that case `value`/`onChange`
// use the shape {keyword: string} | {date: <date>} instead of a bare date value.
const FloatingDateTimePicker = ({
    label, keywordsLabel, type, keywordOptions, value, onChange, disabled, className, placeholder
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const [calendarHeight, setCalendarHeight] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const calendarSectionRef = useRef(null);

    const hasKeywords = keywordOptions && keywordOptions.length > 0;
    const dateValue = hasKeywords ? value?.date : value;
    const keywordValue = hasKeywords ? value?.keyword : null;

    // Centers the panel on the trigger once its actual (possibly wider, with the keyword column)
    // width is known; until then, falls back to the trigger's own left edge so there's something
    // reasonable to render before the first measurement.
    const computePosition = () => {
        if (!triggerRef.current) {
            return null;
        }
        const rect = triggerRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const top = rect.bottom + 4;
        if (panelRef.current) {
            const panelWidth = panelRef.current.offsetWidth;
            const left = Math.max(8, Math.min(centerX - panelWidth / 2, window.innerWidth - panelWidth - 8));
            return {top, left, centerX, ready: true};
        }
        return {top, left: rect.left, centerX, ready: false};
    };

    const open = () => {
        // The trigger calls this on every click/focus - once open, re-clicking it must be a no-op
        // rather than re-running the open sequence.
        if (disabled || isOpen) {
            return;
        }
        setPosition(computePosition());
        setCalendarHeight(null);
        setIsOpen(true);
    };

    useLayoutEffect(() => {
        if (!isOpen || !panelRef.current || (position && position.ready)) {
            return;
        }
        setPosition(computePosition());
    });

    // CSS can't cap the keyword column to the calendar's height on its own, so measure the
    // calendar once rendered and use that as the keyword column's height cap.
    useLayoutEffect(() => {
        if (!isOpen || !hasKeywords || !calendarSectionRef.current || calendarHeight) {
            return;
        }
        setCalendarHeight(calendarSectionRef.current.offsetHeight);
    });

    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }
        const handleOutsideClick = (event) => {
            // Uses composedPath(), not .contains(event.target) - a click inside the panel can
            // trigger a synchronous re-render (e.g. advancing day -> hour -> minute swaps out the
            // whole grid, since items are keyed by index and each level has different content) that
            // detaches the clicked node from the DOM before this same event finishes bubbling here.
            // A detached node is never "contained" by anything, so contains() would wrongly read as
            // an outside click; composedPath() is a snapshot of the event's path at dispatch time,
            // unaffected by DOM changes made while the event is still bubbling.
            const path = event.composedPath ? event.composedPath() : [event.target];
            const clickedTrigger = triggerRef.current && path.includes(triggerRef.current);
            const clickedPanel = panelRef.current && path.includes(panelRef.current);
            if (!clickedTrigger && !clickedPanel) {
                setIsOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };
        const handleReposition = () => setPosition(computePosition());

        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        window.addEventListener("scroll", handleReposition, true);
        window.addEventListener("resize", handleReposition);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
            window.removeEventListener("scroll", handleReposition, true);
            window.removeEventListener("resize", handleReposition);
        };
    }, [isOpen]);

    // onChange fires as the user drills day -> hour -> minute; only onComplete (DateTimePicker's
    // final-level signal) should close the panel, so a "datetime" pick isn't cut short after just
    // the day is clicked.
    const selectDate = (newDate) => {
        onChange && onChange(hasKeywords ? {date: newDate} : newDate);
    };
    const completeDate = () => setIsOpen(false);

    const selectKeyword = (code) => {
        onChange && onChange({keyword: code});
        setIsOpen(false);
    };

    const displayValue = () => {
        if (hasKeywords && keywordValue) {
            const option = keywordOptions.find(opt => opt.code === keywordValue);
            return option ? option.label : keywordValue;
        }
        return dateValue ? dateToString(dateValue, type !== "datetime", true) : "";
    };

    return (
        <>
            <label ref={triggerRef}
                   className={"field has-float-label dropdown date-time "
                       + (placeholder ? "has-default-placeholder " : "") + (className || "")}
                   style={{marginBottom: "0.25rem"}}>
                <input
                    type="text"
                    readOnly
                    value={displayValue()}
                    disabled={disabled}
                    placeholder={placeholder || " "}
                    className="input"
                    onClick={open}
                    onFocus={open}
                />
                <span>{label}</span>
                <div className="dropdown-indicator right-decoration fz fz-calendar-alt solid"/>
            </label>
            {isOpen && position && createPortal(
                <div ref={panelRef}
                     className={"floating-portal-panel" + (hasKeywords ? " has-keywords" : "")}
                     style={{position: "fixed", top: position.top, left: position.left, visibility: position.ready ? "visible" : "hidden"}}>
                    {hasKeywords &&
                        <div className="picker-section">
                            <div className="section-label">{keywordsLabel}</div>
                            <div className="keyword-panel" style={{maxHeight: calendarHeight || undefined}}>
                                {keywordOptions.map(option => (
                                    <div key={option.code}
                                         className={"keyword-chip" + (keywordValue === option.code ? " active" : "")}
                                         onClick={() => selectKeyword(option.code)}>
                                        {option.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                    <div className="picker-section" ref={calendarSectionRef}>
                        {hasKeywords && <div className="section-label">{label}</div>}
                        <DateTimePicker type={type} value={dateValue} onChange={selectDate} onComplete={completeDate}/>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

FloatingDateTimePicker.propTypes = {
    label: PropTypes.string,
    keywordsLabel: PropTypes.string,
    type: PropTypes.oneOf(["date", "datetime"]),
    keywordOptions: PropTypes.arrayOf(PropTypes.shape({
        code: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })),
    value: PropTypes.oneOfType([
        PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date),
        PropTypes.shape({
            keyword: PropTypes.string,
            date: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
        }),
    ]),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    // Shown as native grayed-out placeholder text when the field is empty - e.g. the column's
    // configured default value.
    placeholder: PropTypes.string,
};

FloatingDateTimePicker.defaultProps = {
    type: "date",
    keywordsLabel: "Keywords",
    disabled: false,
};

export default FloatingDateTimePicker;
